import { useState, useEffect, useCallback, useRef } from 'react';

const axios = require('axios').default;

// Soft credits: donations OTHER parties made that are credited to this
// recipient (a contact or an organization). Mirrors useDonations (paginated
// list + total). `softCreditsUrl` points at the recipient's soft_credits.json
// endpoint; defaults to the contact's for existing callers.
export default function useSoftCredits({ team, contact, csrfToken, softCreditsUrl, enabled = true }) {
  const [softCredits, setSoftCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [count, setCount] = useState(null);
  const [total, setTotal] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const activeFetchRef = useRef(null);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  const url = softCreditsUrl ||
    (contact && contact.id ? `/teams/${team?.id}/crm/fans/${contact.id}/soft_credits.json` : null);

  useEffect(() => {
    if (!enabled) return;
    if (!team || !url) return;

    const fetchSoftCredits = async () => {
      if (activeFetchRef.current) activeFetchRef.current.cancel('stale');
      const source = axios.CancelToken.source();
      activeFetchRef.current = source;

      try {
        setLoading(true);
        setError('');
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const response = await axios.get(
          url,
          { params: { page, per_page: perPage }, cancelToken: source.token },
        );
        const data = response.data || {};
        const rows = data.soft_credits || [];
        const pagination = data.pagination || {};

        setSoftCredits(rows);
        setCount(typeof data.soft_credit_count === 'number' ? data.soft_credit_count : rows.length);
        setTotal(data.soft_credit_total || '');
        setTotalEntries(pagination.total_entries || rows.length || 0);
        setTotalPages(pagination.total_pages || 1);
      } catch (err) {
        if (axios.isCancel(err)) return;
        setError('Failed to load soft credits');
      } finally {
        if (activeFetchRef.current === source) {
          activeFetchRef.current = null;
          setLoading(false);
        }
      }
    };

    fetchSoftCredits();
  }, [team?.id, url, csrfToken, page, perPage, reloadToken, enabled]);

  return {
    softCredits, loading, error, count, total,
    page, setPage, perPage, totalEntries, totalPages, reload,
  };
}
